/**
 * The MIT License (MIT)
 *
 * Copyright © 2018 Andreas Mehlsen andmehlsen@gmail.com
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated
 * documentation files (the "Software"), to  * deal in the Software without restriction, including without
 * limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies
 * of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT
 * LIMITED TO THE WARRANTIES OF MERCHANTABILITY,  * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
 * IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER  * LIABILITY,
 * WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE
 * OR THE USE OR OTHER DEALINGS  * IN THE SOFTWARE
 */
/**
 * Traverses the slots of the open shadowroots and returns all children matching the query.
 * We need to traverse each child-depth one at a time because if an element should be skipped
 * (for example because it is hidden) we need to skip all of it's children. If we use querySelectorAll("*")
 * the information of whether the children is within a hidden parent is lost.
 * @param {ShadowRoot | HTMLElement} root
 * @param skipNode
 * @param isMatch
 * @param {number} maxDepth
 * @param {number} depth
 * @returns {HTMLElement[]}
 */
export function queryShadowRoot(root, skipNode, isMatch, maxDepth = 20, depth = 0) {
    const matches = [];
    // If the depth is above the max depth, abort the searching here.
    if (depth >= maxDepth) {
        return matches;
    }
    const processElement = (element) => {
        // Check if the element and its descendants should be skipped
        if (skipNode(element)) {
            return;
        }
        // If the element matches we always add it
        if (isMatch(element)) {
            matches.push(element);
        }
        if (element.shadowRoot != null) {
            // If the element has a shadow root we need to traverse it
            matches.push(...queryShadowRoot(element.shadowRoot, skipNode, isMatch, maxDepth, depth + 1));
        }
        else if (element.tagName === 'SLOT') {
            // If the child is a slot we need to traverse each assigned node
            matches.push(...traverseSlot(element));
        }
        else {
            // Traverse the children of the element
            matches.push(...queryShadowRoot(element, skipNode, isMatch, maxDepth, depth + 1));
        }
    };
    // Traverses a slot element
    const traverseSlot = (slot) => {
        // Only check nodes that are of the type Node.ELEMENT_NODE
        // Read more here https://developer.mozilla.org/en-US/docs/Web/API/Node/nodeType
        const assignedNodes = slot.assignedNodes().filter((node) => node.nodeType === 1);
        if (assignedNodes.length > 0) {
            for (const assignedNode of assignedNodes) {
                processElement(assignedNode);
            }
        }
        return [];
    };
    // Go through each child and continue the traversing if necessary
    // Even though the typing says that children can't be undefined, Edge 15 sometimes gives an undefined value.
    // Therefore we fallback to an empty array if it is undefined.
    const children = Array.from(root.children || []);
    for (const childElement of children) {
        processElement(childElement);
    }
    return matches;
}
//# sourceMappingURL=query-shadow-root.js.map